import { useState, useRef, useEffect, useCallback } from 'react'
import { apiUrl, authFetch } from '../../lib/api'
import { isCloudMode } from '../../lib/storageMode'
import { setActiveProject as setActiveRunningProject } from '../../lib/activeProject'
import { io, type Socket } from 'socket.io-client'

const WS_URL = ((import.meta as any).env?.VITE_WS_URL ?? 'ws://localhost:3001').replace(/^ws/, 'http')

function getToken(): string {
  try { return JSON.parse(localStorage.getItem('pulse-auth') ?? '{}')?.state?.token ?? '' }
  catch { return '' }
}

interface ProjectInfo {
  type: 'static' | 'vite' | 'next' | 'react' | 'node' | 'python'
  name: string
  runCmd: string
  entryFile?: string
}

export function PreviewPanel() {
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const [key, setKey] = useState(0)
  const [devPort, setDevPort] = useState<number | null>(null)
  const [projects, setProjects] = useState<ProjectInfo[]>([])
  const [activeProject, setActiveProject] = useState<ProjectInfo | null>(null)
  const [running, setRunning] = useState(false)
  const [loading, setLoading] = useState(false)
  const devPortRef = useRef<number | null>(null)
  const ignoreUntilRef = useRef(0)
  const socketRef = useRef<Socket | null>(null)

  const refresh = useCallback(() => setKey((k) => k + 1), [])

  // Connect socket
  useEffect(() => {
    const token = getToken()
    const socket = io(WS_URL, { transports: ['websocket', 'polling'], auth: { token }, query: { token } })
    socketRef.current = socket

    socket.on('devserver:ready', ({ port }: { port: number }) => {
      if (Date.now() < ignoreUntilRef.current) return
      devPortRef.current = port
      setDevPort(port)
      setRunning(true)
      setLoading(false)
      setKey((k) => k + 1)
    })

    socket.on('devserver:stopped', () => {
      devPortRef.current = null
      setDevPort(null)
      setRunning(false)
    })

    socket.on('connect', () => {
      socket.emit('devserver:query')
    })

    return () => { socket.disconnect() }
  }, [])

  // Detect projects
  useEffect(() => {
    const detect = () => detectProjects()
    const timer = setTimeout(detect, 500)
    const handler = () => {
      // Clear running state when files change (mode switch)
      setRunning(false)
      setDevPort(null)
      devPortRef.current = null
      setProjects([])
      setActiveProject(null)
      setTimeout(detect, 500)
    }
    window.addEventListener('pulse:files-changed', handler)
    window.addEventListener('pulse:storage-mode-changed', handler)
    return () => {
      clearTimeout(timer)
      window.removeEventListener('pulse:files-changed', handler)
      window.removeEventListener('pulse:storage-mode-changed', handler)
    }
  }, [])

  async function detectProjects() {
    const token = getToken()
    if (!token) return
    try {
      const res = await authFetch(apiUrl('/api/files/list'))
      if (!res.ok) return
      const tree = await res.json() as any[]
      if (tree.length === 0) { setProjects([]); return }

      const fileNames = new Set(tree.filter((n: any) => n.type === 'file').map((n: any) => n.name))
      const folders = tree.filter((n: any) => n.type === 'folder')
      const found: ProjectInfo[] = []

      // Sub-folder projects (most common: my-app/)
      for (const folder of folders) {
        const children = folder.children ?? []
        if (!children.some((c: any) => c.name === 'package.json')) continue
        try {
          const pkgRes = await authFetch(apiUrl(`/api/files/read?path=${encodeURIComponent(folder.name + '/package.json')}`))
          const pkg = JSON.parse((await pkgRes.json()).content)
          const deps = { ...pkg.dependencies, ...pkg.devDependencies }
          let type: ProjectInfo['type'] = 'node'
          let cmd = 'npm start'
          if (deps['vite']) { type = 'vite'; cmd = 'npm run dev -- --host' }
          else if (deps['react-scripts']) { type = 'react'; cmd = 'npm start' }
          else if (deps['next']) { type = 'next'; cmd = 'npm run dev -- -H 0.0.0.0' }
          else if (pkg.scripts?.dev) { cmd = 'npm run dev' }
          found.push({ type, name: folder.name, runCmd: `cd ${folder.name} && ${cmd}` })
        } catch {}
      }

      // Root package.json
      if (fileNames.has('package.json') && found.length === 0) {
        try {
          const pkgRes = await authFetch(apiUrl('/api/files/read?path=package.json'))
          const pkg = JSON.parse((await pkgRes.json()).content)
          const deps = { ...pkg.dependencies, ...pkg.devDependencies }
          if (deps['vite']) found.push({ type: 'vite', name: pkg.name ?? 'Vite App', runCmd: 'npm run dev -- --host' })
          else if (deps['next']) found.push({ type: 'next', name: pkg.name ?? 'Next.js', runCmd: 'npm run dev -- -H 0.0.0.0' })
          else if (pkg.scripts?.dev) found.push({ type: 'node', name: pkg.name ?? 'Node App', runCmd: 'npm run dev' })
          else if (pkg.scripts?.start) found.push({ type: 'node', name: pkg.name ?? 'Node App', runCmd: 'npm start' })
        } catch {}
      }

      // Python at root
      if (fileNames.has('app.py')) found.push({ type: 'python', name: 'Flask', runCmd: 'python app.py' })
      else if (fileNames.has('main.py')) found.push({ type: 'python', name: 'Python', runCmd: 'python main.py' })

      // Python in subfolders
      for (const folder of folders) {
        const children = folder.children ?? []
        if (children.some((c: any) => c.name === 'app.py')) {
          found.push({ type: 'python', name: `${folder.name}`, runCmd: `cd ${folder.name} && pip install -r requirements.txt 2>nul && python app.py` })
        } else if (children.some((c: any) => c.name === 'main.py') && !children.some((c: any) => c.name === 'package.json')) {
          found.push({ type: 'python', name: `${folder.name}`, runCmd: `cd ${folder.name} && pip install -r requirements.txt 2>nul && python main.py` })
        }
      }

      // Static HTML
      if (fileNames.has('index.html') && !fileNames.has('package.json')) {
        found.push({ type: 'static', name: 'index.html', runCmd: '', entryFile: 'index.html' })
      }
      // Detect any .html files at root as static projects
      for (const f of tree.filter((n: any) => n.type === 'file' && n.name.endsWith('.html') && n.name !== 'index.html')) {
        found.push({ type: 'static', name: f.name, runCmd: '', entryFile: f.name })
      }
      for (const folder of folders) {
        const children = folder.children ?? []
        if (children.some((c: any) => c.name === 'index.html') && !children.some((c: any) => c.name === 'package.json')) {
          found.push({ type: 'static', name: `${folder.name}/`, runCmd: '', entryFile: `${folder.name}/index.html` })
        }
      }

      setProjects(found)
      if (!activeProject && found.length > 0) setActiveProject(found[0])
    } catch {}
  }

  const handleRun = async () => {
    if (!activeProject) return

    // Static HTML — just show in iframe
    if (activeProject.type === 'static' && activeProject.entryFile) {
      setRunning(true)
      setActiveRunningProject(activeProject.name) // Set active project scope
      return
    }

    if (!activeProject.runCmd) return

    // Set active project for chat scoping
    setActiveRunningProject(activeProject.name)

    // Cloud mode: restore from DB to disk, then run locally
    if (isCloudMode()) {
      setRunning(true)
      setLoading(true)
      const projectName = activeProject.runCmd.match(/cd (\S+)/)?.[1] || activeProject.name
      try {
        await authFetch(apiUrl('/api/files/restore-cloud'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ slug: projectName }),
        })
      } catch {}
      // Override cmd to use .cloud-cache path
      const cacheCmd = activeProject.runCmd.replace(`cd ${projectName}`, `cd .cloud-cache/${projectName}`)
      const finalCmd = `Get-Process -Id (Get-NetTCPConnection -LocalPort 5174 -ErrorAction SilentlyContinue).OwningProcess -ErrorAction SilentlyContinue | Stop-Process -Force -ErrorAction SilentlyContinue; ` + cacheCmd.replace(/npm run dev/, 'npm install && npx vite --host --port 5174')
      const token = getToken()
      fetch('http://localhost:3001/api/terminal/kill', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      }).catch(() => {}).finally(() => {
        setTimeout(() => {
          fetch('http://localhost:3001/api/terminal/run', {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ command: finalCmd }),
          }).catch(() => {})
        }, 500)
      })
      return
    }

    setRunning(true)
    setLoading(true)
    setDevPort(null)
    devPortRef.current = null
    ignoreUntilRef.current = Date.now() + 2000

    // Build command
    let cmd = activeProject.runCmd
    // Force port 5174 for vite/react projects to avoid conflicting with PULSE on 5173
    if (activeProject.type === "vite" || activeProject.type === "react") {
      cmd = cmd.replace(/npm run dev.*--host/, 'npm install && npx --yes concurrently "npx vite --host --port 5174" "node server/index.cjs 2>nul || node server/index.js 2>nul"')
    }
    if (activeProject.type !== 'python') {
      if (cmd.includes('cd ')) {
        cmd = cmd.replace(/&& npm run/, '&& npm install && npm run')
      } else {
        cmd = `npm install && ${cmd}`
      }
    }

    // Open terminal panel and send command via HTTP API
    // Terminal runs silently in background
    
    // Prepend port kill to avoid conflicts
    cmd = `Get-NetTCPConnection -LocalPort 5174 -ErrorAction SilentlyContinue | ForEach-Object { Stop-Process -Id $_.OwningProcess -Force -ErrorAction SilentlyContinue }; ${cmd}`

    // Use HTTP endpoint to run command (bypasses WebSocket issues)
    const token = getToken()
    // Kill existing terminal first so it starts fresh from workspace root
    fetch('http://localhost:3001/api/terminal/kill', {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
    }).catch(() => {}).finally(() => {
      setTimeout(() => {
        fetch('http://localhost:3001/api/terminal/run', {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ command: cmd }),
        }).catch(() => {})
      }, 500)
    })
  }

  const handleStop = () => {
    const token = getToken()
    // Kill terminal (kills all child processes including vite)
    fetch('http://localhost:3001/api/terminal/kill', {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
    }).catch(() => {})
    setRunning(false)
    setLoading(false)
    setDevPort(null)
    devPortRef.current = null
    setActiveRunningProject(null) // Clear active project scope
  }

  // What to show in the iframe
  let iframeSrc = ''
  if (devPort) {
    iframeSrc = `http://localhost:${devPort}`
  } else if (running && activeProject?.type === 'static' && activeProject.entryFile) {
    iframeSrc = `http://localhost:3001/api/files/preview/${activeProject.entryFile}?token=${getToken()}`
  }

  return (
    <div className="preview-panel">
      {/* Toolbar */}
      <div className="preview-panel__toolbar">
        <button className="preview-panel__btn" onClick={refresh} title="Refresh">↻</button>
        <input className="preview-panel__url" value={devPort ? `localhost:${devPort}` : activeProject?.name ?? '...'} readOnly spellCheck={false} />
        {!running ? (
          <button className="preview-panel__btn" onClick={handleRun} disabled={!activeProject}
            style={{ color: 'var(--accent-2)', border: '1px solid var(--accent-2)', background: 'rgba(95,212,164,0.1)', padding: '0 8px', gap: 4, display: 'flex', alignItems: 'center', fontSize: 10, fontFamily: 'var(--font-mono)', fontWeight: 700 }}>
            ▶ Run
          </button>
        ) : (
          <button className="preview-panel__btn" onClick={handleStop}
            style={{ color: 'var(--red)', border: '1px solid var(--red)', background: 'rgba(240,112,112,0.1)', padding: '0 8px', gap: 4, display: 'flex', alignItems: 'center', fontSize: 10, fontFamily: 'var(--font-mono)', fontWeight: 700 }}>
            ■ Stop
          </button>
        )}
        {iframeSrc && (
          <button className="preview-panel__btn" onClick={() => window.open(iframeSrc, '_blank')} title="Open in new tab">↗</button>
        )}
      </div>

      {/* Content */}
      {iframeSrc ? (
        <div style={{ flex: 1, position: 'relative', minHeight: 0 }}>
          <iframe ref={iframeRef} key={key} src={iframeSrc}
            style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none', background: '#fff' }}
            title="Preview" />
        </div>
      ) : loading ? (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 16, background: 'var(--bg-1)' }}>
          <div style={{ position: 'relative', width: 48, height: 48 }}>
            <div style={{ position: 'absolute', inset: 0, border: '3px solid var(--border)', borderTopColor: 'var(--accent)', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
            <div style={{ position: 'absolute', inset: 6, border: '3px solid var(--border)', borderBottomColor: 'var(--accent-2)', borderRadius: '50%', animation: 'spin 1.2s linear infinite reverse' }} />
          </div>
          <div style={{ fontSize: 12, fontFamily: 'var(--font-mono)', color: 'var(--text-1)', fontWeight: 600 }}>
            Starting {activeProject?.name}...
          </div>
          <div style={{ fontSize: 10, fontFamily: 'var(--font-mono)', color: 'var(--text-2)' }}>
            Installing dependencies & launching dev server
          </div>
          <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
        </div>
      ) : projects.length > 0 ? (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'auto', background: 'var(--bg-1)' }}>
          <div style={{ padding: '10px 14px', fontSize: 9, fontWeight: 700, color: 'var(--text-2)', letterSpacing: '0.08em', textTransform: 'uppercase', fontFamily: 'var(--font-mono)' }}>
            Detected project{projects.length > 1 ? 's' : ''}
          </div>
          {projects.map((p, i) => (
            <div key={i} onClick={() => setActiveProject(p)} style={{
              padding: '10px 14px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10,
              background: activeProject === p ? 'rgba(124,110,245,0.08)' : 'none',
              border: activeProject === p ? '1px solid var(--accent)' : '1px solid var(--border)',
              borderRadius: 6, margin: '0 10px 6px',
            }}>
              <span style={{ fontSize: 18 }}>⚡</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-0)', fontFamily: 'var(--font-mono)' }}>{p.name}</div>
                <div style={{ fontSize: 10, color: 'var(--text-2)', fontFamily: 'var(--font-mono)' }}>{p.runCmd || 'Static'}</div>
              </div>
            </div>
          ))}
          <div style={{ padding: '10px 14px' }}>
            <button onClick={handleRun} style={{
              width: '100%', padding: '10px', borderRadius: 6, border: '1px solid var(--accent-2)',
              background: 'rgba(95,212,164,0.1)', color: 'var(--accent-2)', cursor: 'pointer',
              fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 700,
            }}>
              ▶ Run {activeProject?.name}
            </button>
          </div>
        </div>
      ) : (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 8, color: 'var(--text-2)', fontSize: 12, fontFamily: 'var(--font-mono)', background: 'var(--bg-1)' }}>
          <span style={{ fontSize: 28 }}>📭</span>
          <span>No project in workspace</span>
          <span style={{ fontSize: 10 }}>Create files or ask AI to scaffold a project</span>
        </div>
      )}
    </div>
  )
}
